// src/pages/shop/CartPage.jsx
//
// Step 3: review the cart. "Proceed to checkout" goes to /checkout.

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ShopNavbar from "../components/shop/ShopNavbar";
import { useCart } from "../context/CartContext";
import { T, F, SHIPPING_FEE, formatKsh } from "../config/shop";

export default function CartPage() {
  const navigate = useNavigate();
  const { items, updateQty, removeFromCart, clearCart, totalItems, totalPrice } = useCart();
  const [message, setMessage] = useState("");

  const changeQty = (item, nextQty) => {
    const result = updateQty(item.cartItemId, nextQty);
    setMessage(result.ok ? "" : result.message);
  };

  const orderTotal = totalPrice + SHIPPING_FEE;

  // ── Empty ─────────────────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div style={S.page}>
        <ShopNavbar showCart={false} />
        <main style={S.emptyContainer}>
          <div style={S.emptyIconBox}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.black} strokeWidth="1.6" strokeLinecap="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
          </div>
          <h1 style={S.emptyTitle}>Your cart is empty</h1>
          <p style={S.emptyText}>Find something handmade and add it to your cart.</p>
          <Link to="/" style={S.shopButton}>Browse products</Link>
        </main>
      </div>
    );
  }

  // ── Cart ──────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{`
        .kz-cart-layout { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 32px; align-items: start; }
        .kz-cart-item { display: grid; grid-template-columns: 110px minmax(0, 1fr) auto; gap: 16px; align-items: center; }
        .kz-link:hover { text-decoration: underline; }
        @media (max-width: 900px) {
          .kz-cart-layout { grid-template-columns: 1fr; gap: 24px; }
          .kz-summary { position: static !important; }
        }
        @media (max-width: 560px) {
          .kz-cart-item { grid-template-columns: 84px minmax(0, 1fr); gap: 12px; }
          .kz-cart-image { width: 84px !important; height: 84px !important; }
          .kz-line-total { grid-column: 2; padding-top: 0 !important; }
        }
      `}</style>

      <ShopNavbar showCart={false} />

      <main style={S.container}>
        <div style={S.pageHeading}>
          <div>
            <h1 style={S.title}>Your cart</h1>
            <p style={S.subtitle}>
              {totalItems} {totalItems === 1 ? "item" : "items"} in your cart
            </p>
          </div>
          <button type="button" onClick={clearCart} style={S.clearButton}>Clear cart</button>
        </div>

        {message && <div role="status" style={S.message}>{message}</div>}

        <div className="kz-cart-layout">
          <section style={S.itemsSection}>
            {items.map((item) => (
              <article key={item.cartItemId} className="kz-cart-item" style={S.cartItem}>
                <div className="kz-cart-image" style={S.itemImageWrapper}>
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.title}
                      style={S.itemImage}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div style={S.noImage}>No image</div>
                  )}
                </div>

                <div style={S.itemDetails}>
                  <Link to={`/product/${item.id}`} className="kz-link" style={S.itemTitle}>
                    {item.title}
                  </Link>

                  <div style={S.itemPrice}>{formatKsh(item.price)}</div>

                  {(item.selectedSize || item.selectedColor) && (
                    <div style={S.variants}>
                      {item.selectedSize && <span>Size: <strong>{item.selectedSize}</strong></span>}
                      {item.selectedColor && <span>Color: <strong>{item.selectedColor}</strong></span>}
                    </div>
                  )}

                  <div style={S.itemBottom}>
                    <div style={S.quantityControl}>
                      <button type="button" aria-label="Decrease quantity" onClick={() => changeQty(item, item.qty - 1)} style={S.quantityButton}>−</button>
                      <span style={S.quantityValue}>{item.qty}</span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() => changeQty(item, item.qty + 1)}
                        disabled={item.stock !== null && item.qty >= item.stock}
                        style={{ ...S.quantityButton, opacity: item.stock !== null && item.qty >= item.stock ? 0.35 : 1 }}
                      >
                        +
                      </button>
                    </div>

                    <button type="button" className="kz-link" onClick={() => removeFromCart(item.cartItemId)} style={S.removeButton}>
                      Remove
                    </button>
                  </div>
                </div>

                <div className="kz-line-total" style={S.lineTotal}>{formatKsh(item.price * item.qty)}</div>
              </article>
            ))}
          </section>

          <aside className="kz-summary" style={S.summary}>
            <h2 style={S.summaryTitle}>Order summary</h2>

            <div style={S.summaryRow}><span>Items</span><span>{totalItems}</span></div>
            <div style={S.summaryRow}><span>Subtotal</span><strong>{formatKsh(totalPrice)}</strong></div>
            <div style={S.summaryRow}><span>Delivery</span><strong>{formatKsh(SHIPPING_FEE)}</strong></div>

            <div style={S.divider} />

            <div style={S.totalRow}><span>Total</span><strong>{formatKsh(orderTotal)}</strong></div>

            <p style={S.deliveryNote}>
              Pay securely with M-Pesa, card or bank transfer. You'll enter your delivery details on the next step.
            </p>

            <button type="button" onClick={() => navigate("/checkout")} style={S.checkoutButton}>
              Proceed to checkout
            </button>

            <Link to="/" style={S.continueButton}>Continue shopping</Link>
          </aside>
        </div>
      </main>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: T.surface, color: T.ink, fontFamily: F.body },
  container: { maxWidth: 1200, margin: "0 auto", padding: "36px 24px 70px" },
  pageHeading: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, marginBottom: 26 },
  title: { margin: 0, fontFamily: F.display, fontSize: 26, lineHeight: 1.2, color: T.ink, fontWeight: 800 },
  subtitle: { margin: "6px 0 0", color: T.muted, fontFamily: F.body, fontSize: 13.5 },
  clearButton: { border: "none", background: "transparent", color: T.error, cursor: "pointer", fontWeight: 700, fontFamily: F.body, fontSize: 13 },
  message: { background: T.yellow, color: T.black, border: `1px solid ${T.black}`, borderRadius: 2, padding: "11px 16px", marginBottom: 18, fontFamily: F.body, fontSize: 13, fontWeight: 700 },
  itemsSection: { display: "grid", gap: 12 },
  cartItem: { border: `1px solid ${T.border}`, borderRadius: 4, padding: 14, background: T.white },
  itemImageWrapper: { width: 110, height: 110, borderRadius: 2, overflow: "hidden", background: "#fafaf8", display: "flex", alignItems: "center", justifyContent: "center" },
  itemImage: { width: "100%", height: "100%", objectFit: "contain", padding: 8, boxSizing: "border-box", display: "block" },
  noImage: { color: T.muted, fontSize: 12, fontFamily: F.body },
  itemDetails: { minWidth: 0 },
  itemTitle: { color: T.ink, textDecoration: "none", fontWeight: 700, fontFamily: F.display, fontSize: 16, display: "block", marginBottom: 6 },
  itemPrice: { fontWeight: 800, marginBottom: 6, fontFamily: F.body, fontSize: 14, color: T.black },
  variants: { display: "flex", flexWrap: "wrap", gap: 12, color: T.muted, fontSize: 12.5, marginBottom: 12, fontFamily: F.body },
  itemBottom: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  quantityControl: { display: "flex", alignItems: "center", border: `1px solid ${T.black}`, borderRadius: 2, overflow: "hidden" },
  quantityButton: { width: 32, height: 32, border: "none", background: T.white, cursor: "pointer", fontSize: 16, fontFamily: F.body },
  quantityValue: { width: 34, textAlign: "center", fontWeight: 700, fontFamily: F.body, fontSize: 13 },
  removeButton: { border: "none", background: "transparent", color: T.error, cursor: "pointer", fontWeight: 700, fontFamily: F.body, fontSize: 12.5 },
  lineTotal: { fontWeight: 800, color: T.black, whiteSpace: "nowrap", alignSelf: "start", paddingTop: 4, fontFamily: F.body, fontSize: 14.5 },
  summary: { border: `1px solid ${T.border}`, borderRadius: 4, padding: 22, position: "sticky", top: 90, background: T.white },
  summaryTitle: { margin: "0 0 20px", fontFamily: F.display, fontSize: 18, fontWeight: 800, color: T.ink },
  summaryRow: { display: "flex", justifyContent: "space-between", gap: 20, marginBottom: 14, color: T.inkSoft, fontFamily: F.body, fontSize: 13.5 },
  divider: { height: 1, background: T.borderSoft, margin: "16px 0" },
  totalRow: { display: "flex", justifyContent: "space-between", gap: 20, fontSize: 17, color: T.ink, marginBottom: 16, fontFamily: F.body, fontWeight: 700 },
  deliveryNote: { color: T.muted, fontSize: 12, lineHeight: 1.6, marginBottom: 18, fontFamily: F.body },
  checkoutButton: { width: "100%", minHeight: 48, border: `2px solid ${T.black}`, borderRadius: 2, background: T.black, color: T.yellow, fontWeight: 700, fontSize: 14, fontFamily: F.body, cursor: "pointer", marginBottom: 10 },
  continueButton: { display: "block", textAlign: "center", color: T.ink, textDecoration: "none", fontWeight: 700, padding: 10, fontFamily: F.body, fontSize: 13 },
  emptyContainer: { minHeight: "70vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "40px 24px" },
  emptyIconBox: { width: 60, height: 60, borderRadius: "50%", border: `2px solid ${T.black}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 },
  emptyTitle: { margin: 0, fontFamily: F.display, fontSize: 22, fontWeight: 800, color: T.ink },
  emptyText: { color: T.muted, margin: "10px 0 24px", fontFamily: F.body, fontSize: 14 },
  shopButton: { display: "inline-block", background: T.black, color: T.yellow, textDecoration: "none", padding: "12px 24px", borderRadius: 2, fontWeight: 700, fontFamily: F.body, fontSize: 13.5 },
};
